import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, Fingerprint, User, Shield, Check, X, 
  Bell, BellRing, Smartphone, Download, Moon, Sun, 
  ChevronRight, Calendar, Clock, Truck, Users, LogOut,
  Crown, Cat, Star, Heart, Rocket, Dog, Bird, Fish, Flame, Zap, Coffee, Music, Gamepad2, Trophy, Diamond, Gift, Sparkles, Edit, Save, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { cn } from '../lib/utils';
import { PWAStatusCard, NotificationPrompt } from '../components/PWAComponents';
import { usePWAInstall, usePushNotifications } from '../hooks/usePWA';

// Icon mapping
const PROFILE_ICONS = {
  crown: { icon: Crown, label: 'Couronne', color: 'text-yellow-400' },
  cat: { icon: Cat, label: 'Chat', color: 'text-orange-400' },
  star: { icon: Star, label: 'Étoile', color: 'text-yellow-300' },
  heart: { icon: Heart, label: 'Cœur', color: 'text-pink-400' },
  rocket: { icon: Rocket, label: 'Fusée', color: 'text-blue-400' },
  shield: { icon: Shield, label: 'Bouclier', color: 'text-cyan-400' },
  dog: { icon: Dog, label: 'Chien', color: 'text-amber-400' },
  bird: { icon: Bird, label: 'Oiseau', color: 'text-sky-400' },
  fish: { icon: Fish, label: 'Poisson', color: 'text-teal-400' },
  flame: { icon: Flame, label: 'Flamme', color: 'text-orange-500' },
  zap: { icon: Zap, label: 'Éclair', color: 'text-yellow-400' },
  coffee: { icon: Coffee, label: 'Café', color: 'text-amber-600' },
  music: { icon: Music, label: 'Musique', color: 'text-purple-400' },
  gamepad: { icon: Gamepad2, label: 'Jeux', color: 'text-green-400' },
  trophy: { icon: Trophy, label: 'Trophée', color: 'text-yellow-500' },
  diamond: { icon: Diamond, label: 'Diamant', color: 'text-cyan-300' },
  gift: { icon: Gift, label: 'Cadeau', color: 'text-red-400' },
  sparkles: { icon: Sparkles, label: 'Étoiles', color: 'text-purple-300' },
};

// Settings Section Component
const SettingsSection = ({ icon: Icon, title, children }) => (
  <div className="glass-panel p-5 animate-slide-up">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
        <Icon size={20} className="text-zinc-400" />
      </div>
      <h2 className="text-lg font-bold text-white uppercase tracking-wide">{title}</h2>
    </div>
    {children}
  </div>
);

// Settings Row Component
const SettingsRow = ({ icon: Icon, title, subtitle, action, onClick, danger = false }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all active:scale-[0.99] ${danger ? 'hover:bg-red-500/10' : ''}`}
  >
    <div className="flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${danger ? 'bg-red-500/20' : 'bg-white/5'}`}>
        <Icon size={20} className={danger ? 'text-red-400' : 'text-zinc-400'} />
      </div>
      <div className="text-left">
        <p className={`font-medium ${danger ? 'text-red-400' : 'text-white'}`}>{title}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action || <ChevronRight size={18} className="text-zinc-600" />}
  </button>
);

// Toggle Component
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={onChange}
    className={`relative w-12 h-7 rounded-full transition-colors ${enabled ? 'bg-red-500' : 'bg-zinc-700'}`}
  >
    <span 
      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
    />
  </button>
);

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const { isAvailable, registerBiometric } = useBiometricAuth();
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileIcon, setProfileIcon] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    prenom: '',
    nom: '',
    email: ''
  });
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    loadSettings();
    
    // Capture PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Check if notifications are enabled
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Initialize profile form when user data is available
  useEffect(() => {
    if (user) {
      setProfileForm({
        prenom: user.prenom || '',
        nom: user.nom || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/auth/biometric-status');
      setBiometricEnabled(response.data.enabled);
      if (response.data.credential_id) {
        localStorage.setItem('biometricCredentialId', response.data.credential_id);
      }
      
      // Load profile icon
      const iconResponse = await api.get('/auth/profile-icon');
      setProfileIcon(iconResponse.data.icon);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableBiometric = async () => {
    setRegistering(true);
    try {
      const success = await registerBiometric();
      if (success) {
        setBiometricEnabled(true);
        const response = await api.get('/auth/biometric-status');
        if (response.data.credential_id) {
          localStorage.setItem('biometricCredentialId', response.data.credential_id);
        }
        toast.success('Touch ID / Face ID activé !');
      }
    } catch (error) {
      console.error('Error enabling biometric:', error);
      toast.error('Erreur lors de l\'activation');
    } finally {
      setRegistering(false);
    }
  };

  const handleDisableBiometric = async () => {
    try {
      await api.post('/auth/disable-biometric');
      setBiometricEnabled(false);
      localStorage.removeItem('biometricCredentialId');
      toast.success('Authentification biométrique désactivée');
    } catch (error) {
      console.error('Error disabling biometric:', error);
      toast.error('Erreur lors de la désactivation');
    }
  };

  const handleEnableNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications activées !');
        
        // Show test notification
        new Notification('BYTRANSPORT', {
          body: 'Les notifications sont maintenant activées',
          icon: '/logo.png',
          badge: '/logo192.png',
          tag: 'test-notification'
        });
      } else {
        toast.error('Permission refusée pour les notifications');
      }
    } else {
      toast.error('Les notifications ne sont pas supportées sur cet appareil');
    }
  };

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Application installée !');
      }
      setDeferredPrompt(null);
    } else {
      // iOS instructions
      toast.info('Sur iOS: Appuyez sur "Partager" puis "Sur l\'écran d\'accueil"');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectIcon = async (iconKey) => {
    try {
      await api.put('/auth/profile-icon', { icon: iconKey });
      setProfileIcon(iconKey);
      setShowIconPicker(false);
      toast.success(iconKey ? 'Icône mise à jour !' : 'Initiales restaurées');
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error updating icon:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveProfile = async () => {
    // Check if any changes were made
    if (
      profileForm.prenom === user?.prenom &&
      profileForm.nom === user?.nom &&
      profileForm.email === user?.email
    ) {
      toast.info('Aucune modification détectée');
      setShowProfileEdit(false);
      return;
    }

    setSavingProfile(true);
    try {
      const response = await api.put('/auth/profile', profileForm);
      if (response.data.success) {
        toast.success('Profil mis à jour avec succès !');
        if (response.data.changes?.length > 0) {
          toast.info(`Les administrateurs ont été notifiés des changements.`);
        }
        if (refreshUser) refreshUser();
        setShowProfileEdit(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  // Render profile avatar
  const renderProfileAvatar = () => {
    if (profileIcon && PROFILE_ICONS[profileIcon]) {
      const IconComponent = PROFILE_ICONS[profileIcon].icon;
      const iconColor = PROFILE_ICONS[profileIcon].color;
      return (
        <IconComponent size={32} className={iconColor} />
      );
    }
    return (
      <span className="text-2xl font-bold text-white">
        {user?.prenom?.[0]}{user?.nom?.[0]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-0 pb-24" data-testid="settings-page">
      {/* Profile Card */}
      <div className="glass-panel p-6 animate-slide-up">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowIconPicker(true)}
                className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-neon-red hover:scale-105 transition-transform"
                data-testid="profile-icon-button"
              >
                {renderProfileAvatar()}
              </button>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-zinc-800 border-2 border-red-500 flex items-center justify-center">
                <Edit size={12} className="text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate">{user?.prenom} {user?.nom}</h1>
              <p className="text-sm text-zinc-500 break-all">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 uppercase tracking-wider">
                {user?.role}
              </span>
            </div>
          </div>
          <Button
            variant="brandOutline"
            size="sm"
            onClick={() => setShowProfileEdit(true)}
            className="flex-shrink-0"
            data-testid="edit-profile-button"
          >
            <Edit size={16} className="mr-1" />
            Modifier
          </Button>
        </div>
      </div>

      {/* Profile Edit Modal */}
      {showProfileEdit && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={cn(
            "rounded-2xl border w-full max-w-md overflow-hidden animate-slide-up",
            theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
          )}>
            <div className={cn(
              "flex items-center justify-between p-4 border-b",
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>
              <div className="flex items-center gap-2">
                <User size={20} className="text-red-400" />
                <h2 className={cn("text-lg font-bold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>Modifier mon profil</h2>
              </div>
              <button 
                onClick={() => setShowProfileEdit(false)}
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                )}
              >
                <X size={20} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-500">
                  🔐 Les administrateurs seront notifiés de toute modification via BYCHECKSECURITY
                </p>
              </div>
              
              <div>
                <Label htmlFor="edit-prenom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Prénom</Label>
                <Input
                  id="edit-prenom"
                  value={profileForm.prenom}
                  onChange={(e) => setProfileForm({...profileForm, prenom: e.target.value})}
                  className={cn(
                    "mt-1",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  data-testid="input-edit-prenom"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-nom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Nom</Label>
                <Input
                  id="edit-nom"
                  value={profileForm.nom}
                  onChange={(e) => setProfileForm({...profileForm, nom: e.target.value})}
                  className={cn(
                    "mt-1",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  data-testid="input-edit-nom"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-email" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>
                  <Mail size={14} className="inline mr-1" />
                  Email
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                  className={cn(
                    "mt-1",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                  data-testid="input-edit-email"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className={cn(
                    "flex-1",
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  )}
                  onClick={() => {
                    setProfileForm({
                      prenom: user?.prenom || '',
                      nom: user?.nom || '',
                      email: user?.email || ''
                    });
                    setShowProfileEdit(false);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  variant="brand"
                  className="flex-1"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  data-testid="save-profile-button"
                >
                  {savingProfile ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Enregistrement...
                    </span>
                  ) : (
                    <>
                      <Save size={16} className="mr-1" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/10 w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">Choisir une icône</h2>
              <button 
                onClick={() => setShowIconPicker(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} className="text-zinc-400" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Reset to initials option */}
              <button
                onClick={() => handleSelectIcon(null)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl mb-3 transition-all ${
                  !profileIcon 
                    ? 'bg-red-500/20 border-2 border-red-500' 
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-lg font-bold text-white">
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </div>
                <div className="text-left">
                  <p className="font-medium text-white">Mes initiales</p>
                  <p className="text-xs text-zinc-500">Afficher mes initiales par défaut</p>
                </div>
                {!profileIcon && <Check size={20} className="ml-auto text-red-400" />}
              </button>
              
              {/* Icon grid */}
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-3">Choisir une icône</p>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(PROFILE_ICONS).map(([key, { icon: IconComp, label, color }]) => (
                  <button
                    key={key}
                    onClick={() => handleSelectIcon(key)}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all ${
                      profileIcon === key 
                        ? 'bg-red-500/20 border-2 border-red-500' 
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                    title={label}
                  >
                    <IconComp size={28} className={color} />
                    <span className="text-[10px] text-zinc-400 mt-1 truncate w-full text-center">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Navigation */}
      <SettingsSection icon={Smartphone} title="Navigation">
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={() => navigate('/planning')}
            className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
          >
            <Calendar size={24} className="text-cyan-400 mb-2" />
            <span className="text-sm text-white">Planning</span>
          </button>
          <button 
            onClick={() => navigate('/employees')}
            className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
          >
            <Users size={24} className="text-green-400 mb-2" />
            <span className="text-sm text-white">Salariés</span>
          </button>
          <button 
            onClick={() => navigate('/vehicles')}
            className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
          >
            <Truck size={24} className="text-amber-400 mb-2" />
            <span className="text-sm text-white">Flotte</span>
          </button>
          <button 
            onClick={() => navigate('/incidents')}
            className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
          >
            <Bell size={24} className="text-red-400 mb-2" />
            <span className="text-sm text-white">Incidents</span>
          </button>
        </div>
      </SettingsSection>

      {/* Security Section */}
      <SettingsSection icon={Shield} title="Sécurité">
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <Fingerprint size={20} className="text-zinc-400" />
              </div>
              <div>
                <p className="font-medium text-white">Touch ID / Face ID</p>
                <p className="text-xs text-zinc-500">
                  {!isAvailable ? 'Non disponible' : biometricEnabled ? 'Activé' : 'Désactivé'}
                </p>
              </div>
            </div>
            {isAvailable && (
              <Toggle 
                enabled={biometricEnabled} 
                onChange={biometricEnabled ? handleDisableBiometric : handleEnableBiometric} 
              />
            )}
          </div>
        </div>
      </SettingsSection>

      {/* Notifications Section */}
      <SettingsSection icon={BellRing} title="Notifications">
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <Bell size={20} className="text-zinc-400" />
              </div>
              <div>
                <p className="font-medium text-white">Notifications Push</p>
                <p className="text-xs text-zinc-500">
                  Recevez des alertes sur votre téléphone
                </p>
              </div>
            </div>
            <Toggle 
              enabled={notificationsEnabled} 
              onChange={handleEnableNotifications} 
            />
          </div>
          
          {notificationsEnabled && (
            <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-2 text-green-400">
                <Check size={16} />
                <span className="text-sm font-medium">Notifications activées</span>
              </div>
              <p className="text-xs text-zinc-400 mt-1">
                Vous recevrez des rappels pour vos tâches et alertes
              </p>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* PWA Installation */}
      <SettingsSection icon={Download} title="Application Mobile">
        <div className="space-y-3">
          <SettingsRow
            icon={Smartphone}
            title="Installer l'application"
            subtitle="Ajoutez BYTRANSPORT à votre écran d'accueil"
            onClick={handleInstallPWA}
            action={
              <span className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-xs font-medium">
                Installer
              </span>
            }
          />
          
          <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
            <p className="text-sm text-cyan-400 font-medium mb-2">
              Fonctionnalités disponibles :
            </p>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li className="flex items-center gap-2">
                <Check size={12} className="text-cyan-400" />
                Accès rapide depuis l'écran d'accueil
              </li>
              <li className="flex items-center gap-2">
                <Check size={12} className="text-cyan-400" />
                Notifications push pour les rappels
              </li>
              <li className="flex items-center gap-2">
                <Check size={12} className="text-cyan-400" />
                Mode plein écran sans navigateur
              </li>
              <li className="flex items-center gap-2">
                <Check size={12} className="text-cyan-400" />
                Mode hors-ligne avec cache
              </li>
              <li className="flex items-center gap-2">
                <Check size={12} className="text-cyan-400" />
                Raccourcis vers Planning, Tâches...
              </li>
            </ul>
          </div>
          
          {/* PWA Status Card */}
          <PWAStatusCard />
        </div>
      </SettingsSection>

      {/* Logout */}
      <div className="glass-panel p-5 animate-slide-up">
        <SettingsRow
          icon={LogOut}
          title="Déconnexion"
          subtitle="Fermer votre session"
          onClick={handleLogout}
          danger
        />
      </div>

      {/* Version */}
      <div className="text-center pb-8">
        <p className="text-xs text-zinc-600">
          BYTRANSPORT v2.1 • PWA Edition
        </p>
        <p className="text-[10px] text-zinc-700 mt-1">
          Powered by Yassine Belhadj
        </p>
      </div>
    </div>
  );
}
